import "./DownloadLink.scss";

import { useSelector } from "utils/redux";

import { KaboodleToaster } from "../common";
import { getDownloadFilenameForDocument } from "utils/document-utils";

export const initiateDownload = (
  downloadUrl: string,
  accessToken: string | null,
  filename: string,
  extension?: string | null,
) => {
  if (!accessToken) {
    KaboodleToaster.error("Authentication required. Please log in again.");
    return;
  }
  try {
    fetch(downloadUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Error downloading file: ${filename}`);
        }
        // When ENABLE_PRESIGNED_URL_DOWNLOADS is on, /api/v1/file_proxy
        // returns {"url": "<presigned>"} JSON instead of the file bytes.
        // Click through to the presigned URL — its signed query params
        // travel with the navigation and S3 serves the file. We can't
        // fetch() the S3 URL from JS (bucket has no CORS for our origin
        // and S3 rejects Authorization on presigned URLs).
        const responseContentType = res.headers.get("content-type") || "";
        if (responseContentType.includes("application/json")) {
          const payload = (await res.json()) as { url?: string };
          if (!payload?.url) {
            throw new Error(
              "Backend returned JSON without a presigned download URL",
            );
          }
          const a = document.createElement("a");
          a.href = payload.url;
          a.download = getDownloadFilenameForDocument(filename, extension);
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          return;
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = getDownloadFilenameForDocument(filename, extension);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("Download error:", error);
        KaboodleToaster.error(
          "Error downloading file. If the problem persists, please contact support.",
        );
      });
  } catch (error) {
    console.error("Download error:", error);
    KaboodleToaster.error(
      "Error downloading file. If the problem persists, please contact support.",
    );
  }
};

type Props = {
  url: string;
  filename: string;
  title: string;
  icon: string;
  disabled?: boolean;
  className?: string;
};

const DownloadLink = ({
  url,
  filename,
  title,
  icon,
  disabled = false,
  // f-none stops .flex's own flex:1 from stretching this into a growing
  // flex item whenever a parent lays it out in a row (e.g. a list item
  // with justify-content: space-between), which otherwise makes the
  // hover background balloon across the whole row instead of just the icon.
  // This is the shared default, so it applies to every DownloadLink, not
  // just RatedAgeRequestModal's; checked the other two call sites (RRE
  // table cells) and their column width was already fixed by the grid
  // either way, so this doesn't change how they render.
  className = "material-icons flex f-none ai-c jc-c no-pd h-100 download-btn",
}: Props) => {
  const accessToken = useSelector((state) => state.main.accessToken);

  const handleDownload = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!url) {
      return;
    }
    initiateDownload(url, accessToken, filename);
  };

  return (
    <a
      className={className + (disabled ? " disabled" : " w-hover")}
      title={disabled ? "Download not available" : title}
      href={url}
      onClick={handleDownload}
      data-icon={icon}
    ></a>
  );
};

export default DownloadLink;
